import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import api from '../services/api';
import toast from '../utils/toast';

// Google reCAPTCHA Site Key - Replace with your own key
// Get your keys from: https://www.google.com/recaptcha/admin
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'; // Test key for development

const PublicFormPage = () => {
  const { token } = useParams();
  const [customPage, setCustomPage] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaError, setCaptchaError] = useState(false);

  useEffect(() => {
    fetchPublicForm();
  }, [token]);

  const fetchPublicForm = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/custom-pages/public/${token}`);
      if (response.data.success) {
        setCustomPage(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching public form:', error);
      toast.error('Failed to load form');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    const requiredFields = customPage.fields.filter(f => f.required);
    for (const field of requiredFields) {
      if (!formData[field.name] || formData[field.name] === '') {
        toast.error(`${field.label} is required`);
        return;
      }
    }

    // Validate CAPTCHA if enabled
    if (customPage.publicUrlSettings?.enableCaptcha && !captchaToken) {
      setCaptchaError(true);
      toast.error('Please complete the CAPTCHA verification');
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post(`/custom-pages/public/${token}/submit`, {
        data: formData,
        captchaToken: captchaToken
      });

      if (response.data.success) {
        setSubmitted(true);
        toast.success(response.data.message || 'Form submitted successfully!');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(error.response?.data?.message || 'Failed to submit form');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCaptchaChange = (value) => {
    setCaptchaToken(value);
    setCaptchaError(false);
  };

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!customPage) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Form Not Found</h1>
          <p className="text-gray-600">This public form does not exist or is no longer available.</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
            <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Success!</h2>
          <p className="text-gray-600">
            {customPage.publicUrlSettings?.successMessage || 'Your submission has been received successfully!'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {customPage.publicUrlSettings?.customTitle || customPage.name}
          </h1>
          {customPage.publicUrlSettings?.customDescription && (
            <p className="text-gray-600 mb-6 whitespace-pre-wrap">{customPage.publicUrlSettings.customDescription}</p>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {customPage.fields.map((field, index) => (
              <div key={index}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {field.label}
                  {field.required && <span className="text-red-500 ml-1">*</span>}
                </label>

                {field.type === 'text' && (
                  <input
                    type="text"
                    value={formData[field.name] || ''}
                    onChange={(e) => handleFieldChange(field.name, e.target.value)}
                    placeholder={field.placeholder}
                    required={field.required}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                )}

                {field.type === 'number' && (
                  <input
                    type="number"
                    value={formData[field.name] || ''}
                    onChange={(e) => handleFieldChange(field.name, e.target.value)}
                    placeholder={field.placeholder}
                    required={field.required}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                )}

                {field.type === 'date' && (
                  <input
                    type="date"
                    value={formData[field.name] || ''}
                    onChange={(e) => handleFieldChange(field.name, e.target.value)}
                    required={field.required}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                )}

                {field.type === 'dropdown' && (
                  <select
                    value={formData[field.name] || ''}
                    onChange={(e) => handleFieldChange(field.name, e.target.value)}
                    required={field.required}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select...</option>
                    {field.options?.map((option, i) => (
                      <option key={i} value={option}>{option}</option>
                    ))}
                  </select>
                )}

                {field.type === 'multitext' && (
                  <textarea
                    value={formData[field.name] || ''}
                    onChange={(e) => handleFieldChange(field.name, e.target.value)}
                    placeholder={field.placeholder}
                    required={field.required}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                )}
              </div>
            ))}

            {customPage.publicUrlSettings?.enableCaptcha && (
              <div className="bg-gray-100 p-4 rounded-md border border-gray-300">
                <p className="text-sm text-gray-700 mb-2 font-medium">
                  CAPTCHA Verification
                  <span className="text-red-500 ml-1">*</span>
                </p>
                <p className="text-xs text-gray-500 mb-3">
                  Please complete the CAPTCHA verification to submit the form.
                </p>
                <div className="flex justify-center">
                  <ReCAPTCHA
                    sitekey={RECAPTCHA_SITE_KEY}
                    onChange={handleCaptchaChange}
                    onExpired={() => setCaptchaToken('')}
                    onErrored={() => {
                      setCaptchaToken('');
                      toast.error('CAPTCHA verification failed. Please try again.');
                    }}
                  />
                </div>
                {captchaError && (
                  <p className="text-red-500 text-sm mt-2">
                    Please complete the CAPTCHA verification
                  </p>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full px-4 py-3 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Submitting...' : 'Submit'}
            </button>
          </form>
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-sm text-gray-500">
            Powered by Data Manager System
          </p>
        </div>
      </div>
    </div>
  );
};

export default PublicFormPage;

